import type { ReactNode } from "react";

import { visibleLinks, isExternalLink } from "@/components/cms/SectionRenderer";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { ExternalLink } from "@/components/ui/ExternalLink";
import { Section, SectionHeading } from "@/components/ui/Section";
import type { SectionDoc } from "@/content";

/**
 * A closing band: heading and supporting line on the left, buttons on the right.
 *
 * The shape the Our Work, programme and Where we work pages close with. The
 * first button is the solid one when `primary` is "light"; otherwise every
 * button is an outline, which is how those pages have always looked on navy.
 */
export function CtaBand({
  section,
  primary = "ghostLight",
  maxButtons = 2,
  children,
}: {
  section: SectionDoc;
  primary?: "light" | "ghostLight";
  maxButtons?: number;
  children?: ReactNode;
}) {
  const links = visibleLinks(section.ctas).slice(0, maxButtons);
  const tone = section.settings?.tone === "midnight" ? "midnight" : "navy";

  return (
    <Section tone={tone}>
      <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
        {section.title ? <SectionHeading title={section.title} lead={section.lead} tone="light" /> : null}
        {links.length ? (
          <div className="flex shrink-0 flex-wrap gap-3">
            {links.map((link, index) =>
              isExternalLink(link) ? (
                <ExternalLink key={link.href + link.label} href={link.href} variant="buttonLight">
                  {link.label}
                </ExternalLink>
              ) : (
                <ButtonLink key={link.href + link.label} href={link.href} variant={index === 0 ? primary : "ghostLight"}>
                  {link.label}
                  {index === 0 ? <ArrowRight /> : null}
                </ButtonLink>
              ),
            )}
          </div>
        ) : null}
      </div>
      {children}
    </Section>
  );
}
